import React, { useEffect, useRef } from 'react';
import { Row, Col, Image } from "react-bootstrap";
import Container from '../components/Container';
import SectionHeader from '../components/SectionHeader';
import Card from '../components/Card';
import { Target, Eye, Heart, Users } from 'lucide-react';
import { motion, useInView, useAnimation } from 'framer-motion';
import '../styles/components.css';

const Apropos = () => {
  const valeurs = [
    {
      icon: Target,
      title: "Mission",
      text: "Mettre notre expertise en gestion, optimisation et valorisation des déchets au service des particuliers, entreprises et collectivités pour un avenir plus durable."
    },
    {
      icon: Eye,
      title: "Vision",
      text: "Devenir le partenaire de référence pour la transition écologique, en accompagnant chacun dans la réduction de son impact environnemental."
    },
    {
      icon: Heart,
      title: "Engagement",
      text: "Intégrer le développement durable au cœur de nos activités pour rendre l'ensemble de nos actions le plus respectueux possible de l'environnement."
    },
    {
      icon: Users,
      title: "Expertise",
      text: "Travailler en collaboration avec un panel de personnel formé, qualifié et ayant une expérience et une expertise de la gestion et du traitement de déchets."
    }
  ];

  // Refs pour les animations au scroll
  const imageRef = useRef(null);
  const textRef = useRef(null);
  const valeursRef = useRef(null);
  const ctaRef = useRef(null);

  const imageInView = useInView(imageRef, { once: true, margin: "-100px" });
  const textInView = useInView(textRef, { once: true, margin: "-100px" });
  const valeursInView = useInView(valeursRef, { once: true, margin: "-100px" });
  const ctaInView = useInView(ctaRef, { once: true, margin: "-100px" });

  const imageControls = useAnimation();
  const textControls = useAnimation();
  const valeursControls = useAnimation();
  const ctaControls = useAnimation();

  useEffect(() => {
    if (imageInView) {
      imageControls.start("visible");
    }
  }, [imageInView, imageControls]);

  useEffect(() => {
    if (textInView) {
      textControls.start("visible");
    }
  }, [textInView, textControls]);

  useEffect(() => {
    if (valeursInView) {
      valeursControls.start("visible");
    }
  }, [valeursInView, valeursControls]);

  useEffect(() => {
    if (ctaInView) {
      ctaControls.start("visible");
    }
  }, [ctaInView, ctaControls]);

  // Variants d'animation
  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="section" style={{ overflow: 'hidden' }}>
      <Container>
        {/* Hero Section avec animation */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SectionHeader 
            title="Qui sommes-nous ?"
            subtitle="Moov4Goods, votre partenaire pour la gestion durable des déchets"
          />
        </motion.div>

        {/* Section Histoire avec image et texte */}
        <Row className="align-items-center" style={{ marginBottom: 'var(--spacing-4xl)', minHeight: '500px' }}>
          <Col lg={12} className="mx-auto">
            <Row className="align-items-center g-5">
              {/* Image avec animation */}
              <Col lg={6} md={12} className="mb-4 mb-lg-0">
                <motion.div
                  ref={imageRef}
                  initial="hidden"
                  animate={imageControls}
                  variants={fadeInLeft}
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  style={{ position: 'relative' }}
                >
                  <div style={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 'var(--border-radius-xl)',
                    boxShadow: 'var(--shadow-xl)',
                    background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.1) 0%, rgba(2, 132, 199, 0.1) 100%)',
                    padding: 'var(--spacing-md)'
                  }}>
                    <Image 
                      src="/images/quisommesnous.png" 
                      fluid 
                      alt="Qui sommes-nous - Moov4Goods"
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 'var(--border-radius-lg)',
                        display: 'block',
                        transition: 'transform 0.5s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                    {/* Overlay décoratif */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.05) 0%, transparent 50%)',
                      pointerEvents: 'none',
                      borderRadius: 'var(--border-radius-lg)'
                    }} />
                  </div>
                </motion.div>
              </Col>

              {/* Texte avec animation */}
              <Col lg={6} md={12}>
                <motion.div
                  ref={textRef}
                  initial="hidden"
                  animate={textControls}
                  variants={fadeInRight}
                >
                  <div style={{ paddingLeft: 'var(--spacing-lg)' }}>
                    <motion.h2 
                      style={{ 
                        fontSize: 'var(--font-size-4xl)', 
                        fontWeight: 'var(--font-weight-bold)',
                        marginBottom: 'var(--spacing-xl)',
                        color: 'var(--color-text)',
                        lineHeight: 'var(--line-height-tight)'
                      }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={textInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: 0.2, duration: 0.6 }}
                    >
                      Notre histoire
                    </motion.h2>
                    
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={textInView ? { opacity: 1 } : {}}
                      transition={{ delay: 0.4, duration: 0.6 }}
                    >
                      <p style={{ 
                        fontSize: 'var(--font-size-lg)', 
                        lineHeight: 'var(--line-height-relaxed)',
                        color: 'var(--color-text-light)',
                        marginBottom: 'var(--spacing-lg)'
                      }}>
                        <strong style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-xl)' }}>Moov4Goods</strong> est créé en 2025 par 
                        des ingénieurs diplômés de Polytech Nancy en industrie et environnement. Nous mettons notre 
                        expertise en gestion, optimisation et valorisation des déchets au service des particuliers, 
                        entreprises et collectivités.
                      </p>
                      
                      <p style={{ 
                        fontSize: 'var(--font-size-lg)', 
                        lineHeight: 'var(--line-height-relaxed)',
                        color: 'var(--color-text-light)',
                        marginBottom: 'var(--spacing-lg)'
                      }}>
                        Nous sommes à votre écoute pour concevoir une <strong style={{ color: 'var(--color-primary)' }}>
                        solution innovante sur-mesure</strong> adaptée à votre besoin. Nous travaillons en collaboration 
                        avec un panel de personnel formé, qualifié et ayant une expérience et une expertise de la 
                        gestion et du traitement de déchets.
                      </p>
                      
                      <div style={{
                        padding: 'var(--spacing-lg)',
                        background: 'linear-gradient(135deg, rgba(15, 118, 110, 0.05) 0%, rgba(2, 132, 199, 0.05) 100%)',
                        borderRadius: 'var(--border-radius-lg)',
                        borderLeft: '4px solid var(--color-primary)',
                        marginTop: 'var(--spacing-lg)'
                      }}>
                        <p style={{ 
                          fontSize: 'var(--font-size-lg)', 
                          lineHeight: 'var(--line-height-relaxed)',
                          color: 'var(--color-text)',
                          margin: 0,
                          fontStyle: 'italic'
                        }}>
                          En intégrant le développement durable au cœur de nos activités, nous souhaitons rendre 
                          l'ensemble de nos activités le plus respectueux de l'environnement et vous aider vous aussi 
                          à limiter vos impacts environnementaux.
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Section Valeurs avec animations */}
        <motion.div
          ref={valeursRef}
          initial="hidden"
          animate={valeursControls}
          variants={staggerContainer}
        >
          <SectionHeader 
            title="Nos valeurs"
            subtitle="Les principes qui guident notre action au quotidien"
          />

          <div className="grid-services" style={{ marginTop: 'var(--spacing-3xl)' }}>
            {valeurs.map((valeur, index) => {
              const Icon = valeur.icon;
              return (
                <motion.div
                  key={index}
                  variants={staggerItem}
                  whileHover={{ 
                    y: -8,
                    transition: { duration: 0.3 }
                  }}
                >
                  <Card
                    icon={Icon}
                    title={valeur.title}
                    text={valeur.text}
                  />
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA Section avec animation */}
        <motion.div
          ref={ctaRef}
          initial="hidden"
          animate={ctaControls}
          variants={scaleIn}
        >
          <Row style={{ marginTop: 'var(--spacing-4xl)' }}>
            <Col lg={10} className="mx-auto">
              <motion.div 
                className="card-modern"
                whileHover={{ 
                  scale: 1.02,
                  boxShadow: 'var(--shadow-xl)'
                }}
                transition={{ duration: 0.3 }}
                style={{
                  background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)',
                  color: 'white',
                  padding: 'var(--spacing-3xl)',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Effet de fond animé */}
                <motion.div
                  style={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    animation: 'rotate 20s linear infinite'
                  }}
                />
                
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <motion.h3 
                    style={{ 
                      fontSize: 'var(--font-size-3xl)', 
                      fontWeight: 'var(--font-weight-bold)',
                      marginBottom: 'var(--spacing-lg)',
                      color: 'white'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.2 }}
                  >
                    Rejoignez-nous dans notre mission
                  </motion.h3>
                  
                  <motion.p 
                    style={{ 
                      fontSize: 'var(--font-size-xl)', 
                      lineHeight: 'var(--line-height-relaxed)',
                      color: 'rgba(255, 255, 255, 0.95)',
                      marginBottom: 'var(--spacing-xl)',
                      maxWidth: '600px',
                      margin: '0 auto var(--spacing-xl)'
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.4 }}
                  >
                    Ensemble, construisons un avenir plus durable et responsable.
                  </motion.p>
                  
                  <motion.a 
                    href="/contact" 
                    className="btn-modern btn-modern-lg"
                    style={{ 
                      textDecoration: 'none', 
                      display: 'inline-block',
                      backgroundColor: 'white',
                      color: 'var(--color-primary)',
                      border: 'none'
                    }}
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                    }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.6 }}
                  >
                    Nous contacter
                  </motion.a>
                </div>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      </Container>

      {/* Styles pour l'animation de rotation */}
      <style>{`
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default Apropos;
